import { useState, useCallback } from 'react';
import { backendClient } from '@/lib/api-client';
import toast from 'react-hot-toast';

interface IManageAuthData {
  access_token: string;
  token_type: string;
  expires_in: number;
  refresh_token?: string;
  customer_id?: string;
  user_id?: string;
  dialog_token?: string;
  token_issued_at?: number;  // Timestamp when token was issued
}

interface IManageFilePickerConfig {
  iframe_url: string;
  client_id: string;
  customer_id: string;
  dialog_token: string;
}

export const useIManage = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authData, setAuthData] = useState<IManageAuthData | null>(null);
  const [isLoading, setIsLoading] = useState(false);


  const getFilePickerConfig = useCallback(async (overrideAuth?: IManageAuthData): Promise<IManageFilePickerConfig | null> => {
    const authToUse = overrideAuth || authData;
    
    if (!authToUse?.access_token || !authToUse?.customer_id) {
      toast.error('Please authenticate with iManage first');
      return null;
    }

    setIsLoading(true);
    try {
      
      const response = await backendClient.get<IManageFilePickerConfig>('/imanage/file-picker-config', {
        params: {
          access_token: authToUse.access_token,
          customer_id: authToUse.customer_id,
          user_id: authToUse.user_id, // Include user_id if available
        },
      });
      
      return response.data;
    } catch (error: any) {
      console.error('getFilePickerConfig failed:', error);
      toast.error('Failed to load iManage file picker');
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [authData]);


  // Check for stored auth on mount
  const checkStoredAuth = useCallback(() => {
    const stored = sessionStorage.getItem('imanage_auth');
    
    if (stored) {
      try {
        const data = JSON.parse(stored);
        
        // Validate the stored auth data has required fields
        if (data.access_token && data.customer_id) {
          setAuthData(data);
          setIsAuthenticated(true);
          return true;
        } else {
          sessionStorage.removeItem('imanage_auth');
          setIsAuthenticated(false);
        }
      } catch (error) {
        sessionStorage.removeItem('imanage_auth');
        setIsAuthenticated(false);
      }
    } else {
      setIsAuthenticated(false);
    }
    return false;
  }, []);

  // Check if token is expired
  const isTokenExpired = useCallback((auth: IManageAuthData): boolean => {
    if (!auth.token_issued_at || !auth.expires_in) {
      // If we don't have timestamp info, assume token is still valid
      // This happens right after OAuth login
      return false;
    }
    
    const expiryTime = auth.token_issued_at + (auth.expires_in * 1000);
    const now = Date.now();
    
    // Consider token expired if it has less than 60 seconds remaining
    const bufferTime = 60 * 1000;
    const isExpired = (expiryTime - bufferTime) <= now;
    
    return isExpired;
  }, []);

  // Refresh the access token
  const refreshToken = useCallback(async (): Promise<IManageAuthData | null> => {
    if (!authData?.refresh_token) {
      console.error('No refresh token available');
      return null;
    }

    setIsLoading(true);
    try {
      const response = await backendClient.post<IManageAuthData>('/imanage/refresh-token', null, {
        params: {
          refresh_token: authData.refresh_token,
          customer_id: authData.customer_id,
          user_id: authData.user_id,
        },
      });
      
      // Add timestamp to track when token was issued
      const tokenIssuedAt = Date.now();
      const newAuthData = {
        ...response.data,
        token_issued_at: tokenIssuedAt
      };
      
      
      // Store refreshed auth data
      sessionStorage.setItem('imanage_auth', JSON.stringify(newAuthData));
      setAuthData(newAuthData);
      setIsAuthenticated(true);
      return newAuthData;
    } catch (error: any) {
      console.error('Token refresh failed:', error);
      
      // If refresh fails, clear auth and force re-authentication
      logout();
      toast.error('Session expired. Please authenticate with iManage again.');
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [authData]);

  // Enhanced getFilePickerConfig with automatic token refresh
  const getFilePickerConfigWithRefresh = useCallback(async (overrideAuth?: IManageAuthData): Promise<IManageFilePickerConfig | null> => {
    let currentAuth = overrideAuth || authData;
    
    // Check if token needs refresh
    if (currentAuth) {
      if (isTokenExpired(currentAuth)) {
        const refreshedAuth = await refreshToken();
        if (!refreshedAuth) {
          return null;
        }
        currentAuth = refreshedAuth;
      }
    } else {
      toast.error('Please authenticate with iManage first');
      return null;
    }
    
    if (!currentAuth?.access_token || !currentAuth?.customer_id) {
      toast.error('Please authenticate with iManage first');
      return null;
    }

    setIsLoading(true);
    try {
      const response = await backendClient.get<IManageFilePickerConfig>('/imanage/file-picker-config', {
        params: {
          access_token: currentAuth.access_token,
          customer_id: currentAuth.customer_id,
          user_id: currentAuth.user_id,
        },
      });
      
      return response.data;
    } catch (error: any) {
      console.error('Failed to get file picker config:', error);
      
      // If it's a 401, try to refresh token once
      if (error.response?.status === 401 && currentAuth.refresh_token) {
        const refreshedAuth = await refreshToken();
        if (refreshedAuth) {
          // Retry with new token
          return getFilePickerConfig();
        }
      }
      
      toast.error('Failed to load iManage file picker');
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [authData, isTokenExpired, refreshToken, getFilePickerConfig]);

  const logout = useCallback(() => {
    setAuthData(null);
    setIsAuthenticated(false);
    sessionStorage.removeItem('imanage_auth');
  }, []);

  return {
    isAuthenticated,
    authData,
    isLoading,
    getFilePickerConfig: getFilePickerConfigWithRefresh,
    checkStoredAuth,
    logout,
    refreshToken,
    isTokenExpired,
  };
};